import { io } from "socket.io-client";
import React, { useState, useEffect } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { api } from "../shared/axiosInstance";
import { useCompany } from "../../context/CompanyContext";

const PayForJobPostResults = ({ job, onClose, refreshJobPosts }) => {
    const { companyCredits, setCompanyCredits } = useCompany();
    const stripe = useStripe();
    const elements = useElements();

    const [clientSecret, setClientSecret] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [message, setMessage] = useState("");
    const [paymentSucceeded, setPaymentSucceeded] = useState(false);

    // Always prefetch Stripe client secret on mount
    useEffect(() => {
        const fetchClientSecret = async () => {
            try {
                const res = await api.post('/payments/create-intent', { purchaseType: "purchaseDirect", jobId: job._id });
                setClientSecret(res.data.clientSecret);
            } catch (err) {
                console.error("Error fetching client secret:", err);
                setMessage("Failed to initiate payment. Please try again.");
            }
        };
        fetchClientSecret();
    }, [job._id]);

    const handleUseCredits = async () => {
        setProcessing(true);
        try {
            const res = await api.post("/payments/spend-credit", { jobId: job._id });
            setMessage(res.data.message);
            setPaymentSucceeded(true);
            setCompanyCredits((prev) => prev - 1);
            refreshJobPosts();
        } catch (err) {
            console.error("Error spending credit:", err);
            setMessage(err.response?.data?.message || err.response?.data?.error || "Failed to unlock job using credits.");
        }
        setProcessing(false);
        onClose();
    };
    useEffect(() => {
        const socket = io(process.env.REACT_APP_API_URL || "http://localhost:5000");

        const handleJobUnlocked = (data) => {
            if (data.jobId === job._id) {
                refreshJobPosts();
                setPaymentSucceeded(true);
                setMessage("✅ Job unlocked successfully!");
                setProcessing(false);
                onClose();
            }
        };

        socket.on("job-unlocked", handleJobUnlocked);

        return () => {
            socket.off("job-unlocked", handleJobUnlocked);
            socket.disconnect();
        };
    }, [job._id, refreshJobPosts, onClose]);

    const handleStripePayment = async () => {
        if (!clientSecret) {
            setMessage("Payment not initialized yet.");
            return;
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement || !cardElement._complete) {
            setMessage("Please fill in your card details.");
            return;
        }

        setProcessing(true);
        setMessage("Processing payment...");

        try {
            const result = await stripe.confirmCardPayment(clientSecret, {
                payment_method: { card: cardElement },
            });

            if (result.error) {
                setMessage(`Payment failed: ${result.error.message}`);
            } else {
                setMessage("Payment successful! Waiting for unlock confirmation...");
            }
        } catch (err) {
            setMessage("Unexpected error during payment.");
        }
    };

    const today = new Date();
    const expirationDate = new Date(today);
    expirationDate.setDate(expirationDate.getDate() + 30);
    expirationDate.setUTCHours(6, 0, 0, 0); // 10 PM PST = 6 AM UTC next day
    const formattedDate = expirationDate.toLocaleDateString("en-US", { month: 'short', day: 'numeric', year: 'numeric' });


    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-bluegray p-6 rounded-lg shadow-lg w-[500px] relative">
                <button
                    onClick={onClose}
                    className="absolute top-2 right-2 text-lightgray text-2xl font-bold"
                >
                    &times;
                </button>
                <h2 className="text-orange text-xl font-bold mb-4 text-center">
                    Unlock Job Post Results
                </h2>
                <p className="text-lightgray mb-4 text-center">
                    Lock in Targets and unlock candidate results for: <strong>{job.title}</strong>
                </p>
                {/* Tooltip section */}
                <div className="flex justify-center items-center mb-4">
                    <span className="text-lightgray text-sm mr-2">What am I paying for?</span>
                    <div className="relative group">
                        <button className="text-lightblue">ℹ️</button>
                        <div className="absolute bottom-full mb-2 hidden group-hover:block w-72 p-2 text-xs bg-darkblue text-lightgray border border-lightblue rounded shadow-lg z-10">
                            Locks the Targets and unlocks candidate results for 30 days. If purchased today, access will expire on <strong>{formattedDate} at 10 PM PST</strong>.
                        </div>
                    </div>
                </div>
                {/* Use a Credit */}
                <div className="mb-6">
                    <p className="text-lightgray text-center mb-2">
                        You have <span className="font-bold">{companyCredits}</span> credits.
                    </p>
                    <button
                        onClick={handleUseCredits}
                        className="w-full bg-orange hover:bg-deeporange text-darkblue font-bold py-2 rounded"
                        disabled={processing || companyCredits <= 0}
                    >
                        {companyCredits > 0 ? "Use a Credit" : "No Credits Available"}
                    </button>
                </div>

                {/* Divider */}
                <div className="flex items-center my-4">
                    <div className="flex-grow border-t border-lightblue"></div>
                    <span className="mx-3 text-lightgray text-sm">OR</span>
                    <div className="flex-grow border-t border-lightblue"></div>
                </div>

                {/* Stripe Direct Purchase */}
                <div>
                    <CardElement className="mb-4 p-2 bg-deepblue border border-lightblue rounded text-lightgray" />
                    <button
                        onClick={handleStripePayment}
                        className="w-full bg-orange hover:bg-deeporange text-darkblue font-bold py-2 rounded"
                        disabled={!stripe || processing}
                    >
                        {processing ? "Processing..." : "Pay $250"}
                    </button>
                </div>

                {/* Success message */}
                {paymentSucceeded && (
                    <div className="flex flex-col items-center mt-4">
                        <p className="text-lightgray mb-4">Unlock successful!</p>
                        <button
                            onClick={onClose}
                            className="w-full bg-orange hover:bg-deeporange text-darkblue font-bold py-2 rounded"
                        >
                            Close
                        </button>
                    </div>
                )}

                {message && <p className="text-sm mt-2 text-red text-center">{message}</p>}
            </div>
        </div>
    );
};

export default PayForJobPostResults;
